import { LogMessage } from "../helper-func";
function CalculateRectParams(startPoint, endPoint) {
    const x = Math.min(startPoint.x, endPoint.x);
    const y = Math.min(startPoint.y, endPoint.y);
    const w = Math.abs(startPoint.x - endPoint.x);
    const h = Math.abs(startPoint.y - endPoint.y);
    return [x, y, w, h];
}
// A typical workflow to create svg object is:
//  - user select annotation tool;
//  - when user press mouse button, record the start point;
//  - when user start move mouse, create object and attach to root,
//    then update end point;
//  - when the mouse keep moving, continously update the end point;
export class SvgObject {
    static { this.DefaultColor = "red"; }
    static { this.SelectedColor = "blue"; }
    constructor(startPt, callback) {
        this.id = crypto.randomUUID();
        this._startPoint = startPt;
        this._callback = callback;
        this._positionDelta = { x: 0, y: 0 };
    }
    attachEventListener(attach = true) {
        const objs = this.getShapeObjects();
        if (attach && !this._registeredCalback) {
            this._registeredCalback = (event) => {
                this._callback(this, event);
            };
        }
        objs.forEach((obj) => {
            if (attach) {
                obj.addEventListener('click', this._registeredCalback);
            }
            else {
                obj.removeEventListener('click', this._registeredCalback);
            }
        });
    }
    surroundingRectParams() {
        return CalculateRectParams(this._startPoint, this._endPoint);
    }
    // Returns the rect which contains the shape.
    // It's used to show blue surrounding box when the shape is selected.
    surroundingRect() {
        const [x, y, w, h] = this.surroundingRectParams();
        const rect = `
    <g name="sur" stroke="blue" fill="blue" fill-opacity="0.3" visibility="hidden">
      <rect class="svg-surrounding-box" x="${x}" y="${y}"
            width="${w}" height="${h}" stroke-width="1">
      </rect>
    </g>
    `;
        // LogMessage(`In surroundingRect: ${rect}`);
        return rect;
    }
    updateSurroundingRect() {
        const [x, y, w, h] = this.surroundingRectParams();
        const rect = this._node.querySelector('g[name="sur"]>rect');
        rect.setAttribute("x", `${x}`);
        rect.setAttribute("y", `${y}`);
        rect.setAttribute("width", `${w}`);
        rect.setAttribute("height", `${h}`);
    }
    isPointInSurroundingRect(pt) {
        let [x, y, w, h] = this.surroundingRectParams();
        x += this._positionDelta.x;
        y += this._positionDelta.y;
        return pt.x >= x && pt.x <= (x + w) && pt.y >= y && pt.y <= (y + h);
    }
    createHtml() {
        const svgObject = this.defineObject();
        // LogMessage(`svgObjectDef: ${svgObject}`);
        const surrounding = this.surroundingRect();
        // LogMessage(`surroundingRect: ${surrounding}`);
        const html = `<svg id=${this.id}>
      <g name="wrap">
      ${svgObject}
      ${surrounding}
      <g>
    </svg>`;
        return html;
    }
    createSvgNode() {
        const span = document.createElement('span');
        const innerHtml = this.createHtml();
        // LogMessage(`innerHtml: ${innerHtml}`);
        span.innerHTML = innerHtml;
        const node = span.firstChild;
        this._node = node;
        // LogMessage('Original node:');
        // LogMessage(node);
    }
    attachToRoot(root) {
        if (!this._node) {
            this.createSvgNode();
        }
        root.appendChild(this._node);
        this.attachEventListener(true);
    }
    detachFromRoot() {
        this.attachEventListener(false);
        this._node.parentNode.removeChild(this._node);
    }
    // Just set the endpoint to a new value without updating anything else.
    setEndPoint(endPt) {
        this._endPoint = endPt;
    }
    // subclass should overwrite this method to update the shape of svg.
    updateEndPoint(endPt) {
        this.setEndPoint(endPt);
        this.updateSurroundingRect();
    }
    // Move object with position delta
    moveObject(delta) {
        const wrap = this._node.querySelector('g[name="wrap"]');
        this._positionDelta = {
            x: this._positionDelta.x + delta.x,
            y: this._positionDelta.y + delta.y
        };
        wrap.setAttribute("transform", `translate(${this._positionDelta.x} ${this._positionDelta.y})`);
        // LogMessage(`moveObject, delta <${delta.x} ${delta.y}> posDelta: <${this._positionDelta.x}, ${this._positionDelta.y}>`);
    }
    selectObject(s) {
        this._selected = s;
        // Make the rect visible.
        const group2 = this._node.querySelector('g[name="sur"]');
        const visible = s ? 'visible' : 'hidden';
        group2.setAttribute("visibility", visible);
        const color = s ? SvgObject.SelectedColor : SvgObject.DefaultColor;
        const group1 = this._node.querySelector('g[name="obj"]');
        group1.setAttribute("stroke", color);
    }
    get isAttached() {
        return this._node != null;
    }
    get positionDelta() {
        return this._positionDelta;
    }
    setPositionDelta(finalDelta) {
        const delta = {
            x: finalDelta.x - this._positionDelta.x,
            y: finalDelta.y - this._positionDelta.y,
        };
        this.moveObject(delta);
    }
    toJson() {
        const obj = {
            type: this.getTypeName().toLowerCase(),
            start: {
                x: this._startPoint.x,
                y: this._startPoint.y,
            },
        };
        if (this._endPoint) {
            obj["end"] = {
                x: this._endPoint.x,
                y: this._endPoint.y,
            };
        }
        if (this._positionDelta.x != 0 || this._positionDelta.y != 0) {
            obj["delta"] = {
                x: this._positionDelta.x,
                y: this._positionDelta.y,
            };
        }
        return obj;
    }
}
;
class Ellipse extends SvgObject {
    constructor(startPt, callback) {
        super(startPt, callback);
        LogMessage(`Create ellipse at ${startPt.x} ${startPt.y}`);
    }
    calculateParams() {
        const cx = (this._startPoint.x + this._endPoint.x) / 2;
        const cy = (this._startPoint.y + this._endPoint.y) / 2;
        const rx = Math.abs(this._startPoint.x - this._endPoint.x) / 2;
        const ry = Math.abs(this._startPoint.y - this._endPoint.y) / 2;
        return [cx, cy, rx, ry];
    }
    defineObject() {
        const [cx, cy, rx, ry] = this.calculateParams();
        const def = `<g name="obj" stroke="red" fill="none">
      <ellipse cx="${cx}" cy="${cy}" rx="${rx}" ry="${ry}">
      </ellipse>
    </g>`;
        // LogMessage(`In defineObject: ${def}`);
        return def;
    }
    getShapeObjects() {
        const ellipse = this._node.querySelector('g[name="obj"]>ellipse');
        return [ellipse];
    }
    getTypeName() {
        return 'ellipse';
    }
    updateEndPoint(endPt) {
        super.updateEndPoint(endPt);
        const [cx, cy, rx, ry] = this.calculateParams();
        const ellipse = this._node.querySelector('g[name="obj"]>ellipse');
        ellipse.setAttribute("cx", `${cx}`);
        ellipse.setAttribute("cy", `${cy}`);
        ellipse.setAttribute("rx", `${rx}`);
        ellipse.setAttribute("ry", `${ry}`);
    }
}
class Rect extends SvgObject {
    constructor(startPt, callback) {
        super(startPt, callback);
        LogMessage(`Create rect at ${startPt.x} ${startPt.y}`);
    }
    defineObject() {
        const [x, y, w, h] = CalculateRectParams(this._startPoint, this._endPoint);
        const def = `<g name="obj" stroke="red" fill="none">
      <rect x="${x}" y="${y}" width="${w}" height="${h}">
      </rect>
    </g>`;
        return def;
    }
    getShapeObjects() {
        const rect = this._node.querySelector('g[name="obj"]>rect');
        return [rect];
    }
    getTypeName() {
        return 'rect';
    }
    updateEndPoint(endPt) {
        super.updateEndPoint(endPt);
        const [x, y, w, h] = CalculateRectParams(this._startPoint, this._endPoint);
        const rect = this._node.querySelector('g[name="obj"]>rect');
        rect.setAttribute("x", `${x}`);
        rect.setAttribute("y", `${y}`);
        rect.setAttribute("width", `${w}`);
        rect.setAttribute("height", `${h}`);
    }
}
function CalculateAngleWithXAxis(x1, y1, x2, y2) {
    const dx = x2 - x1;
    const dy = y2 - y1;
    let angleRadians = Math.atan2(dy, dx);
    let angleDegrees = angleRadians * (180 / Math.PI);
    return angleDegrees;
}
class Arrow extends SvgObject {
    constructor(startPt, callback) {
        super(startPt, callback);
        LogMessage(`Create arrow at ${startPt.x} ${startPt.y}`);
    }
    defineObject() {
        const [x1, y1, x2, y2] = [this._startPoint.x, this._startPoint.y, this._endPoint.x, this._endPoint.y];
        const angle = CalculateAngleWithXAxis(x1, y1, x2, y2);
        const def = `<g name="obj" stroke="red" fill="none">
      <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"></line>
      <polyline transform="translate(${x2} ${y2}) rotate(${angle})" points="-12,9 0,0 -12,-9" stroke-linecap="square"></polyline>
    </g>`;
        return def;
    }
    getShapeObjects() {
        const line = this._node.querySelector('g[name="obj"]>line');
        const polyline = this._node.querySelector('g[name="obj"]>polyline');
        return [line, polyline];
    }
    getTypeName() {
        return 'arrow';
    }
    updateEndPoint(endPt) {
        super.updateEndPoint(endPt);
        const [x1, y1, x2, y2] = [this._startPoint.x, this._startPoint.y, this._endPoint.x, this._endPoint.y];
        const angle = CalculateAngleWithXAxis(x1, y1, x2, y2);
        const [line, polyline] = this.getShapeObjects();
        line.setAttribute("x2", `${x2}`);
        line.setAttribute("y2", `${y2}`);
        polyline.setAttribute("transform", `translate(${x2} ${y2}) rotate(${angle})`);
        // LogMessage(`updated angles: ${x1} ${y1} ${x2} ${y2} ${angle}`);
    }
}
class DArrow extends SvgObject {
    constructor(startPt, callback) {
        super(startPt, callback);
        LogMessage(`Create darrow at ${startPt.x} ${startPt.y}`);
    }
    defineObject() {
        const [x1, y1, x2, y2] = [this._startPoint.x, this._startPoint.y, this._endPoint.x, this._endPoint.y];
        const angle = CalculateAngleWithXAxis(x1, y1, x2, y2);
        const angle2 = 180 + angle;
        const def = `<g name="obj" stroke="red" fill="none">
      <line x1="${x1}" y1="${y1}" x2="${x2}" y2="${y2}"></line>
      <polyline transform="translate(${x2} ${y2}) rotate(${angle})" points="-12,9 0,0 -12,-9" stroke-linecap="square"></polyline>
      <polyline transform="translate(${x1} ${y1}) rotate(${angle2})" points="-12,9 0,0 -12,-9" stroke-linecap="square"></polyline>
    </g>`;
        return def;
    }
    getShapeObjects() {
        const line = this._node.querySelector('g[name="obj"]>line');
        const polylines = this._node.querySelectorAll('g[name="obj"]>polyline');
        const results = [line];
        polylines.forEach((po) => {
            results.push(po);
        });
        return results;
    }
    getTypeName() {
        return 'darrow';
    }
    updateEndPoint(endPt) {
        super.updateEndPoint(endPt);
        const [x1, y1, x2, y2] = [this._startPoint.x, this._startPoint.y, this._endPoint.x, this._endPoint.y];
        const angle = CalculateAngleWithXAxis(x1, y1, x2, y2);
        const angle2 = 180 + angle;
        const [line, polyline1, polyline2] = this.getShapeObjects();
        line.setAttribute("x2", `${x2}`);
        line.setAttribute("y2", `${y2}`);
        polyline1.setAttribute("transform", `translate(${x2} ${y2}) rotate(${angle})`);
        polyline2.setAttribute("transform", `translate(${x1} ${y1}) rotate(${angle2})`);
        LogMessage(`updated angles: ${x1} ${y1} ${x2} ${y2} ${angle}`);
    }
}
class Draw extends SvgObject {
    constructor(startPt, callback) {
        super(startPt, callback);
        this._points = new Array();
        this._points.push(startPt);
        LogMessage(`Create draw at ${startPt.x} ${startPt.y}`);
    }
    surroundingRectParams() {
        let [xmin, ymin] = [this._startPoint.x, this._startPoint.y];
        let [xmax, ymax] = [xmin, ymin];
        for (const p of this._points) {
            if (xmin > p.x)
                xmin = p.x;
            if (xmax < p.x)
                xmax = p.x;
            if (ymin > p.y)
                ymin = p.y;
            if (ymax < p.y)
                ymax = p.y;
        }
        return [xmin, ymin, xmax - xmin, ymax - ymin];
    }
    getPathDef() {
        let d = `M ${this._startPoint.x} ${this._startPoint.y}`;
        for (let i = 1; i < this._points.length; i++) {
            const p = this._points[i];
            d += ` L ${p.x} ${p.y}`;
        }
        return d;
    }
    defineObject() {
        const d = this.getPathDef();
        const def = `<g name="obj" stroke="red" fill="none">
      <path d="${d}"></path>
      </g>`;
        return def;
    }
    getShapeObjects() {
        const path = this._node.querySelector('g[name="obj"]>path');
        return [path];
    }
    getTypeName() {
        return 'draw';
    }
    setEndPoint(endPt) {
        super.setEndPoint(endPt);
        this._points.push(endPt);
    }
    updateEndPoint(endPt) {
        super.updateEndPoint(endPt);
        // The length of this._points > 2 since startPoint and endPoint are added before.
        const p1 = this._points[this._points.length - 1];
        const p0 = this._points[this._points.length - 2];
        // (endPt.y - p1.y) / (endPt.x - p1.x) == (p1.y - p0.y) / (p1.x - p0.x)
        if ((endPt.y - p1.y) * (p1.x - p0.x) == (p1.y - p0.y) * (endPt.x - p1.x)) {
            this._points[this._points.length - 1] = endPt; // overwrite the last point.
        }
        else {
            this._points.push(endPt);
        }
        const d = this.getPathDef();
        const path = this._node.querySelector('g[name="obj"]>path');
        path.setAttribute("d", d);
    }
    toJson() {
        const obj = super.toJson();
        obj["points"] = this._points.map((p) => {
            return {
                x: p.x,
                y: p.y
            };
        });
        return obj;
    }
}
export class Text extends SvgObject {
    constructor(startPt, callback) {
        super(startPt, callback);
        LogMessage(`Create text at ${startPt.x} ${startPt.y}`);
    }
    surroundingRectParams() {
        if (!this._surroundingRect) {
            this._surroundingRect = [this._startPoint.x, this._startPoint.y, 20, 10];
        }
        return this._surroundingRect;
    }
    updateText(text, svgRoot) {
        this._text = text;
        if (text.length == 0) {
            return;
        }
        this.attachToRoot(svgRoot);
        // Find the surrounding box.
        const textEle = this.getShapeObjects()[0];
        const bbox = textEle.getBBox({ fill: true, stroke: true });
        this._surroundingRect = [bbox.x, bbox.y, bbox.width, bbox.height];
        this.updateSurroundingRect();
        if (this.textDoneCb) {
            this.textDoneCb(this);
        }
    }
    endTextInput(svgRoot) {
        if (this._textInput) {
            const inputEle = this._textInput.querySelector('input');
            const text = inputEle.value;
            inputEle.value = "";
            this._textInput.classList.add('hidden');
            this.updateText(text, svgRoot);
            LogMessage(`endTextInput: ${text}`);
        }
    }
    startTextInput(root, svgRoot) {
        const textInput = root.querySelector('#textInput');
        textInput.style.left = `${this._startPoint.x}px`;
        textInput.style.top = `${this._startPoint.y}px`;
        const inputEle = textInput.querySelector('input');
        const listener = (event) => {
            event.stopPropagation();
            inputEle.removeEventListener("change", listener);
            this.endTextInput(svgRoot);
        };
        inputEle.addEventListener("change", listener);
        inputEle.addEventListener("keydown", (event) => {
            LogMessage("inputelement keydown to prevent document");
            event.stopPropagation(); // Prevent event bubbling to document keydown.
        });
        textInput.classList.remove("hidden"); // Show the popup.
        inputEle.focus();
        this._textInput = textInput;
    }
    defineObject() {
        const def = `<g name="obj" stroke-width=1 stroke="red" fill="red">
      <text class="svg-text" x="${this._startPoint.x}" y="${this._startPoint.y}"
            dominant-baseline="hanging" paint-order="stroke" stroke-linejoin="round">
      ${this._text}
      </text>
    </g>`;
        return def;
    }
    getShapeObjects() {
        const text = this._node.querySelector('g[name="obj"]>text');
        return [text];
    }
    getTypeName() {
        return 'text';
    }
    // Text is the only object which requires fill.
    selectObject(s) {
        super.selectObject(s);
        const color = s ? SvgObject.SelectedColor : SvgObject.DefaultColor;
        const group1 = this._node.querySelector('g[name="obj"]');
        group1.setAttribute("fill", color);
    }
    toJson() {
        const obj = super.toJson();
        obj["text"] = this._text;
        // LogMessage('Text object serialized to json:');
        // LogMessage(obj);
        return obj;
    }
}
export function CreateSvgObject(svgType, pt, callback) {
    switch (svgType) {
        case "ellipse":
            return new Ellipse(pt, callback);
        case "rect":
            return new Rect(pt, callback);
        case "arrow":
            return new Arrow(pt, callback);
        case "darrow":
            return new DArrow(pt, callback);
        case "draw":
            return new Draw(pt, callback);
        case "text":
            return new Text(pt, callback);
        default:
            throw Error(`unknown svg type ${svgType}`);
    }
}
export function CreateSvgObjectFromJson(svgParent, json, callback) {
    const type = json["type"];
    const startPt = json["start"];
    const svgObj = CreateSvgObject(type, startPt, callback);
    if ("text" in json) {
        const text = json["text"];
        if (text.length == 0) {
            return null;
        }
        const textObj = svgObj;
        textObj.updateText(text, svgParent);
    }
    else if ("points" in json) {
        const drawObj = svgObj;
        for (const pt of json["points"]) {
            drawObj.setEndPoint(pt);
        }
        svgObj.attachToRoot(svgParent);
    }
    else {
        svgObj.setEndPoint(json["end"]);
        svgObj.attachToRoot(svgParent);
    }
    if ("delta" in json) {
        svgObj.moveObject(json["delta"]);
    }
    return svgObj;
}
